import { AdminUser, AuditEntry, featureLabel } from './api';

// Turns an audit row into a sentence an account manager can read. The raw
// details JSON is the source of truth, but nobody should have to parse it to
// answer "who turned this on?".

export interface AuditSentence {
  icon: string;
  text: string;
  detail?: string;
}

/** One seat changing state when the pool was resized. */
interface SeatMove {
  port: number;
  old_state: string;
  new_state: string;
}

const val = (d: Record<string, unknown>, k: string): string | undefined => {
  const v = d?.[k];
  return v === undefined || v === null || v === '' ? undefined : String(v);
};

function whoFor(entry: AuditEntry, users: AdminUser[]): string {
  if (!entry.target_user_id) return 'everyone at this customer';
  const u = users.find((x) => x.id === entry.target_user_id);
  return u ? u.email : 'one user';
}

export function describeAudit(entry: AuditEntry, users: AdminUser[]): AuditSentence {
  const d = (entry.details ?? {}) as Record<string, unknown>;

  switch (entry.action) {
    case 'flag.set': {
      const feature = featureLabel(String(d.feature ?? ''));
      const on = d.enabled === true;
      const target = d.level === 'tenant' ? 'everyone at this customer' : whoFor(entry, users);
      return {
        icon: on ? '✅' : '🚫',
        text: `${on ? 'Enabled' : 'Disabled'} ${feature} for ${target}.`
      };
    }

    case 'entitlement.set': {
      const feature = featureLabel(String(d.feature ?? ''));
      const seats = val(d, 'seats');
      const expires = val(d, 'expires_at');
      return {
        icon: '🧾',
        text: `Recorded a purchase: ${feature}${
          seats ? ` — ${seats} seats` : ' — unlimited seats'
        }.`,
        detail: expires ? `Expires ${expires}.` : undefined
      };
    }

    case 'entitlement.delete':
      return {
        icon: '🗑️',
        text: `Removed the purchase record for ${featureLabel(String(d.feature ?? ''))}.`,
        detail: 'Partner admins can no longer assign it.'
      };

    case 'user.update': {
      const before = (d.before ?? {}) as Record<string, unknown>;
      const change = (d.change ?? {}) as Record<string, unknown>;
      const email = val(d, 'email') ?? whoFor(entry, users);
      const parts: string[] = [];
      if (change.base_message_limit !== undefined) {
        parts.push(
          `message limit ${before.base_message_limit ?? '—'} → ${change.base_message_limit}`
        );
      }
      if (change.disabled !== undefined) {
        parts.push(change.disabled ? 'account disabled' : 'account re-enabled');
      }
      if (change.expiration_date !== undefined) {
        const next = val(change, 'expiration_date');
        parts.push(next ? `expiry set to ${next}` : 'expiry cleared');
      }
      if (change.english_only !== undefined) {
        parts.push(
          change.english_only ? 'English only on' : 'English only off'
        );
      }
      return {
        icon: change.disabled === true ? '⛔' : '👤',
        text: `Changed ${email}: ${parts.length ? parts.join(', ') : 'no visible change'}.`
      };
    }

    case 'customer.update': {
      const change = (d.change ?? {}) as Record<string, unknown>;
      const parts: string[] = [];
      if (change.additional_prompt !== undefined) parts.push('extra prompt instructions');
      if (change.max_message_limit !== undefined)
        parts.push(`message ceiling → ${change.max_message_limit}`);
      if (change.max_users !== undefined) parts.push(`user ceiling → ${change.max_users}`);
      return {
        icon: '⚙️',
        text: `Updated the customer: ${parts.length ? parts.join(', ') : 'settings'}.`
      };
    }

    case 'data.description': {
      const table = val(d, 'table') ?? 'a table';
      const had = val(d, 'before');
      const after = val(d, 'after');
      return {
        icon: '📘',
        text: `${had ? 'Rewrote' : 'Wrote'} the business description of ${table}.`,
        detail: after ? `Now: "${after.slice(0, 140)}${after.length > 140 ? '…' : ''}"` : 'Description cleared.'
      };
    }

    case 'data.table_access': {
      const custom = d.custom === true;
      return {
        icon: custom ? '🔒' : '🔓',
        text: custom
          ? `Put ${whoFor(entry, users)} on a hand-picked table list.`
          : `Put ${whoFor(entry, users)} back on the tenant's default tables.`
      };
    }
    case 'data.visibility': {
      const visible = d.visible === true;
      return {
        icon: visible ? '👁️' : '🙈',
        text: `${visible ? 'Gave' : 'Removed'} ${whoFor(entry, users)} ${
          visible ? 'access to' : 'access from'
        } one data table.`
      };
    }

    case 'data.chat_availability': {
      // The one audit line where saying what did NOT change matters as much.
      // An operator reading this weeks later needs to know the nightly load
      // kept running, or they will go looking for missing data that is there.
      const table = val(d, 'table') ?? 'a topic';
      const on = d.new_value === true;
      return {
        icon: on ? '💬' : '🤐',
        text: on
          ? `Made ${table} available in Signals Chat again.`
          : `Took ${table} out of Signals Chat.`,
        detail: on
          ? undefined
          : 'The nightly ETL and the stored data are unchanged; the topic is only out of conversations.'
      };
    }

    case 'platform.setting': {
      // The audit trail for the numbers that decide how many people the
      // platform holds. It has to read as a sentence, because the moment
      // anyone comes looking is the moment something went wrong.
      const key = val(d, 'key') ?? 'a platform setting';
      const from = val(d, 'old_value');
      const to = val(d, 'new_value');
      const moved = Array.isArray(d.seats_moved) ? (d.seats_moved as SeatMove[]) : [];
      return {
        icon: '🎚️',
        text:
          from !== undefined
            ? `Changed ${key} from ${from} to ${to}.`
            : `Set ${key} to ${to}.`,
        detail: moved.length
          ? moved.map((m) => `port ${m.port}: ${m.old_state} → ${m.new_state}`).join(' · ')
          : val(d, 'applies') === 'new containers'
            ? 'Applies to containers started from now on; running ones keep their limits.'
            : undefined
      };
    }

    case 'permission.grant':
    case 'permission.revoke': {
      const granted = entry.action === 'permission.grant';
      const who = val(d, 'target_email') ?? whoFor(entry, users);
      const perm = val(d, 'permission') === 'MANAGE_PLATFORM_SETTINGS'
        ? 'the right to change platform settings'
        : (val(d, 'permission') ?? 'a permission');
      return {
        icon: granted ? '🔑' : '🔒',
        text: granted ? `Gave ${who} ${perm}.` : `Took ${perm} away from ${who}.`
      };
    }

    default:
      return { icon: '•', text: entry.action, detail: JSON.stringify(d) };
  }
}
